<script setup>
import { cond, propEq, T } from 'ramda';
import { isNotNullAndNotEmpty } from '@/utilities';

const props = defineProps({
  message: {
    type: String,
    default: '',
    required: true,
  },
  type: {
    validator(value) {
      return ['info', 'success', 'warning', 'error', 'simple', 'expiration-warning'].includes(value);
    },
    default: 'warning',
  },
});

const alertAttributes = cond([
  [propEq('info', 'type'), () => ({
    id: 'alert-border-1',
    class: 'flex items-center p-4 mb-4 text-blue-800 border-t-4 border-blue-300 bg-blue-50 dark:text-blue-400 dark:bg-gray-800 dark:border-blue-800',
    buttonClass: 'ms-auto -mx-1.5 -my-1.5 bg-blue-50 text-blue-500 rounded-lg focus:ring-2 focus:ring-blue-400 p-1.5 hover:bg-blue-200 inline-flex items-center justify-center h-8 w-8 dark:bg-gray-800 dark:text-blue-400 dark:hover:bg-gray-700',
  })],
  [propEq('error', 'type'), () => ({
    id: 'alert-border-2',
    class: 'flex items-center p-4 mb-4 text-red-800 border-t-4 border-red-300 bg-red-50 dark:text-red-400 dark:bg-gray-800 dark:border-red-800',
    buttonClass: 'ms-auto -mx-1.5 -my-1.5 bg-red-50 text-red-500 rounded-lg focus:ring-2 focus:ring-red-400 p-1.5 hover:bg-red-200 inline-flex items-center justify-center h-8 w-8 dark:bg-gray-800 dark:text-red-400 dark:hover:bg-gray-700',
  })],
  [propEq('success', 'type'), () => ({
    id: 'alert-border-3',
    class: 'flex items-center p-4 mb-4 text-green-800 border-t-4 border-green-300 bg-green-50 dark:text-green-400 dark:bg-gray-800 dark:border-green-800',
    buttonClass: 'ms-auto -mx-1.5 -my-1.5 bg-green-50 text-green-500 rounded-lg focus:ring-2 focus:ring-green-400 p-1.5 hover:bg-green-200 inline-flex items-center justify-center h-8 w-8 dark:bg-gray-800 dark:text-green-400 dark:hover:bg-gray-700',
  })],
  [propEq('warning', 'type'), () => ({
    id: 'alert-border-4',
    class: 'flex items-center p-4 mb-4 text-yellow-800 border-t-4 border-yellow-300 bg-yellow-50 dark:text-yellow-300 dark:bg-gray-800 dark:border-yellow-800',
    buttonClass: 'ms-auto -mx-1.5 -my-1.5 bg-yellow-50 text-yellow-500 rounded-lg focus:ring-2 focus:ring-yellow-400 p-1.5 hover:bg-yellow-200 inline-flex items-center justify-center h-8 w-8 dark:bg-gray-800 dark:text-yellow-300 dark:hover:bg-gray-700',
  })],
  [propEq('expiration-warning', 'type'), () => ({
    id: 'alert-border-5',
    class: 'flex items-center p-4 mb-4 text-yellow-800 bg-warningYellow dark:text-yellow-300 dark:bg-gray-800 dark:border-yellow-800',
  })],
  [T, () => ({
    id: 'alert-border-5',
    class: 'flex items-center p-4 border-t-4 border-gray-300 bg-gray-50 dark:bg-gray-800 dark:border-gray-600',
    buttonClass: 'ms-auto -mx-1.5 -my-1.5 bg-gray-50 text-gray-500 rounded-lg focus:ring-2 focus:ring-gray-400 p-1.5 hover:bg-gray-200 inline-flex items-center justify-center h-8 w-8 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white',
  })],
])(props);

const emit = defineEmits(['close-alert']);
</script>

<template>
  <div
    v-if="isNotNullAndNotEmpty(props.message)"
    v-bind="alertAttributes"
    role="alert"
  >
    <slot name="icon">
      <svg
        class="flex-shrink-0 w-4 h-4"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"
        />
      </svg>
    </slot>

    <div class="ms-3 text-sm font-medium">
      <span v-html="props.message" />
      <slot />
    </div>



    <button
      v-if="alertAttributes.buttonClass"
      @click="emit('close-alert')"
      type="button"
      :class="alertAttributes.buttonClass"
      aria-label="Close"
    >
      <span class="sr-only">Dismiss</span>
      <svg
        class="w-3 h-3"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 14 14"
      >
        <path
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
        />
      </svg>
    </button>
  </div>
</template>
